import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const HeroBanner = styled.section`
  background: linear-gradient(135deg, #2d5016 0%, #4a7c59 100%);
  color: white;
  padding: 6rem 2rem 4rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d5016;
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ValueCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const ValueImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ValueContent = styled.div`
  padding: 1.5rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d5016;
  margin-bottom: 0.75rem;
`;

const ValueDescription = styled.p`
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const ContentImageSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 6rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-bottom: 4rem;
  }

  ${(props) =>
    props.$reverse &&
    `
    direction: rtl;
    
    > * {
      direction: ltr;
    }
  `}
`;

const ImageContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 2.25rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 1.5rem;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 1.75rem;
    }
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #333;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #2d5016;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  margin-top: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: #1a3a0e;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(45, 80, 22, 0.3);
  }
`;

const BottomBanner = styled.section`
  background: linear-gradient(135deg, #2d5016 0%, #4a7c59 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const BottomBannerContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const BottomBannerTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BottomBannerSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SignUpButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.25rem 3rem;
  background: white;
  color: #2d5016;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }
`;

const About = () => {
  return (
    <Container>
      {/* Hero Banner */}
      <HeroBanner>
        <HeroContent>
          <HeroTitle>AFFORDABLE AND HEALTHY MEAL SUBSCRIPTION</HeroTitle>
          <HeroSubtitle>
            Built right here in Guyana, connecting you with local meal prep
            vendors and nutrition-focused food providers for fresh, wholesome
            meals delivered or picked up with ease
          </HeroSubtitle>
        </HeroContent>
      </HeroBanner>

      {/* Our Values Section 1 */}
      <Section>
        <SectionTitle>Our Values</SectionTitle>
        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <ValueImage>
              <img src="/images/affordable.jpg" alt="Simple & Affordable" />
            </ValueImage>
            <ValueContent>
              <ValueTitle>Simple & Affordable</ValueTitle>
              <ValueDescription>
                Committed to making healthy eating simple, affordable, and part
                of everyday life for everyone.
              </ValueDescription>
            </ValueContent>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ValueImage>
              <img
                src="/images/fresh_and_wholesome.jpg"
                alt="Fresh & Wholesome"
              />
            </ValueImage>
            <ValueContent>
              <ValueTitle>Fresh & Wholesome</ValueTitle>
              <ValueDescription>
                Fresh, wholesome meals from local meal prep vendors and
                nutrition-focused food providers.
              </ValueDescription>
            </ValueContent>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ValueImage>
              <img src="/images/delicious.jpg" alt="Delicious Possibilities" />
            </ValueImage>
            <ValueContent>
              <ValueTitle>Delicious Possibilities</ValueTitle>
              <ValueDescription>
                Unlocking delicious, new meal possibilities with a vast
                chef-crafted menu.
              </ValueDescription>
            </ValueContent>
          </ValueCard>
        </ValuesGrid>

        {/* Our Values Section 2 */}
        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <ValueImage>
              <img src="/images/supporting local.png" alt="Supporting Local" />
            </ValueImage>
            <ValueContent>
              <ValueTitle>Supporting Local</ValueTitle>
              <ValueDescription>
                Passionate about supporting local businesses and helping
                customers discover better food options.
              </ValueDescription>
            </ValueContent>
          </ValueCard>

          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ValueImage>
              <span>❤️</span>
            </ValueImage>
            <ValueContent>
              <ValueTitle>Wellness & Community</ValueTitle>
              <ValueDescription>
                Promoting wellness, convenience, and community through great
                food that brings families together.
              </ValueDescription>
            </ValueContent>
          </ValueCard>
        </ValuesGrid>
      </Section>

      {/* Content Image Section 1 */}
      <Section>
        <ContentImageSection>
          <ImageContainer>
            <img
              src="/images/image.jpg"
              alt="Here to Change the Way People Eat"
            />
          </ImageContainer>
          <TextContent>
            <h2>Here to Change the Way People Eat. Forever</h2>
            <p>
              At NourishGY, we believe healthy eating should be simple,
              affordable, and part of everyday life. Built right here in Guyana,
              our app connects you with local meal prep vendors and
              nutrition-focused food providers so you can get fresh, wholesome
              meals delivered or picked up with ease.
            </p>
            <p>
              We're passionate about supporting local businesses and helping
              customers discover better food options. NourishGY is more than an
              app — it's a movement to promote wellness, convenience, and
              community through great food.
            </p>
            <p>
              Because good food nourishes our bodies. And great food nourishes
              happiness.
            </p>
          </TextContent>
        </ContentImageSection>

        {/* Content Image Section 3 */}
        <ContentImageSection>
          <ImageContainer>
            <img src="/images/image.jpg" alt="How We Got Here" />
          </ImageContainer>
          <TextContent>
            <h2>How We Got Here</h2>
            <p>
              NourishGY was born from a simple idea: to make healthy eating
              simple, affordable, and part of everyday life for Guyanese
              families. Built right here in Guyana, we saw an opportunity to
              connect local meal prep vendors and nutrition-focused food
              providers with customers who want fresh, wholesome meals.
            </p>
            <p>
              What started as a vision to support local businesses and help
              customers discover better food options has grown into a movement
              that promotes wellness, convenience, and community through great
              food.
            </p>
            <p>
              Our philosophy remains the same: NourishGY is more than an app —
              it's a commitment to making healthy eating accessible, supporting
              local vendors, and bringing families together around nutritious,
              delicious meals.
            </p>
          </TextContent>
        </ContentImageSection>
      </Section>

      {/* Bottom Banner */}
      <BottomBanner>
        <BottomBannerContent>
          <BottomBannerTitle>GET A TASTE</BottomBannerTitle>
          <BottomBannerSubtitle>
            Connect with local meal prep vendors and nutrition-focused food
            providers. Get fresh, wholesome meals delivered or picked up with
            ease.
          </BottomBannerSubtitle>
          <SignUpButton to="/meal-plans">SIGN UP TODAY</SignUpButton>
        </BottomBannerContent>
      </BottomBanner>
    </Container>
  );
};

export default About;
